import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Badge } from"@/components/ui/badge";
import { Button } from"@/components/ui/button";
import { Package, CheckCircle, Clock, DollarSign, AlertTriangle, Calendar, Home, Building2, ChevronDown, ChevronUp } from"lucide-react";
import { toast } from"sonner";
import { useNavigate } from"react-router-dom";

const SalesDashboard = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [dashboard, setDashboard] = useState(null);
 const [loading, setLoading] = useState(true);
 const [expandedMonths, setExpandedMonths] = useState({});
 const navigate = useNavigate();

 useEffect(() => {
 loadDashboard();
 }, []);

 const loadDashboard = async () => {
 try {
 const response = await axios.get("/dashboard/sales");
 setDashboard(response.data);
 // Auto-expand first 3 months
 if (response.data.upcoming_payments_grouped) {
 const initialExpanded = {};
 response.data.upcoming_payments_grouped.slice(0, 3).forEach(month => {
 initialExpanded[month.month_key] = true;
 });
 setExpandedMonths(initialExpanded);
 }
 } catch (error) {
 toast.error(t('messages.loadFailed'));
 } finally {
 setLoading(false);
 }
 };

 const toggleMonth = (monthKey) => {
 setExpandedMonths(prev => ({
 ...prev,
 [monthKey]: !prev[monthKey]
 }));
 };

 const formatCurrency = (amount) => {
 return new Intl.NumberFormat('en-US', {
 style: 'currency',
 currency: 'EUR',
 minimumFractionDigits: 0,
 maximumFractionDigits: 0,
 }).format(amount);
 };

 const formatDate = (dateStr) => {
 try {
 return new Date(dateStr).toLocaleDateString('en-GB', {
 day: 'numeric',
 month: 'short',
 year: 'numeric'
 });
 } catch {
 return dateStr;
 }
 };

 const getStatusColor = (status) => {
 switch (status?.toUpperCase()) {
 case 'PAID':
 return 'bg-emerald-500/10 text-emerald-700 border-green-200/60';
 case 'OVERDUE':
 return 'bg-rose-500/10 text-rose-700 border-red-200/60';
 case 'PLANNED':
 case 'UPCOMING':
 return 'bg-blue-500/10 text-blue-700 border-blue-200/60';
 default:
 return 'bg-white/60 text-[#334155] border-slate-200';
 }
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">{t('engineer.loadingDashboard')}</div>
 </div>
 </Layout>
 );
 }

 const stats = [
 {
 title: t('dashboard.totalUnits'),
 value: dashboard?.total_units || 0,
 icon: Package,
 color:"bg-blue-500",
 testId:"total-units-stat"
 },
 {
 title: t('dashboard.available'),
 value: dashboard?.available_units || 0,
 icon: CheckCircle,
 color:"bg-green-500",
 testId:"available-units-stat"
 },
 {
 title: t('salesDashboard.onHold'),
 value: dashboard?.hold_units || 0,
 icon: Clock,
 color:"bg-orange-500",
 testId:"hold-units-stat"
 },
 {
 title: t('salesDashboard.reservedSold'),
 value: (dashboard?.reserved_units || 0) + (dashboard?.sold_units || 0),
 icon: DollarSign,
 color:"bg-emerald-500",
 testId:"reserved-sold-stat"
 },
 ];

 return (
 <Layout user={user} onLogout={onLogout}>
 <div data-testid="sales-dashboard"className="space-y-6">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">{t('salesDashboard.title')}</h1>
 <p className="text-slate-600">{t('salesDashboard.subtitle')}</p>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
 {stats.map((stat) => {
 const Icon = stat.icon;
 return (
 <Card key={stat.title} data-testid={stat.testId} className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-600 mb-1">{stat.title}</p>
 <p className="text-2xl font-semibold text-slate-900 tabular-nums">{stat.value}</p>
 </div>
 <Icon className="h-7 w-7 text-indigo-600"/>
 </div>
 </CardContent>
 </Card>
 );
 })}
 </div>

 <div className="flex gap-4">
 <Button
 data-testid="view-units-button"
 className="bg-slate-900 hover:bg-slate-800:bg-zinc-200"
 onClick={() => navigate("/units")}
 >
 {t('salesDashboard.viewAllUnits')}
 </Button>
 <Button
 data-testid="view-reservations-button"
 variant="outline"
 onClick={() => navigate("/reservations")}
 >
 {t('salesDashboard.manageReservations')}
 </Button>
 </div>

 {dashboard?.expiring_holds_detail && dashboard.expiring_holds_detail.length > 0 && (
 <Card data-testid="expiring-holds-card"className="">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <AlertTriangle className="w-5 h-5 text-orange-500"/>
 {t('salesDashboard.holdsExpiringSoon')}
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-3">
 {dashboard.expiring_holds_detail.map((hold) => (
 <div
 key={hold.id}
 data-testid={`expiring-hold-${hold.id}`}
 className="flex items-center justify-between p-4 bg-orange-50/80 rounded-sm border border-orange-200/60"
 >
 <div>
 <p className="font-medium text-slate-900">{hold.buyer_name}</p>
 <p className="text-sm text-slate-500">{hold.buyer_email}</p>
 <p className="text-[11px] text-slate-500 mt-1">
 {t('salesDashboard.expires')}: {new Date(hold.held_until).toLocaleString()}
 </p>
 </div>
 <Button
 size="sm"
 data-testid={`confirm-deposit-${hold.id}`}
 variant="outline"
 className="hover:bg-emerald-50/80 hover:text-emerald-700 hover:border-emerald-300"
 onClick={() => navigate("/reservations")}
 >
 {t('salesDashboard.confirmDeposit')}
 </Button>
 </div>
 ))}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Upcoming Payments - Grouped by Month */}
 <Card data-testid="upcoming-payments-card"className="">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Calendar className="w-5 h-5 text-blue-600"/>
 {t('dashboard.upcomingPayments')}
 <Badge variant="outline"className="ml-2 bg-white/60">
 {dashboard?.upcoming_payments || 0} total
 </Badge>
 </CardTitle>
 </CardHeader>
 <CardContent>
 {dashboard?.upcoming_payments_grouped && dashboard.upcoming_payments_grouped.length > 0 ? (
 <div className="space-y-4">
 {dashboard.upcoming_payments_grouped.map((monthData) => (
 <div key={monthData.month_key} className="border border-slate-200 rounded-sm overflow-hidden">
 {/* Month Header */}
 <button
 onClick={() => toggleMonth(monthData.month_key)}
 className="w-full flex items-center justify-between p-4 bg-white/50 hover:bg-white/60 transition-colors"
 >
 <div className="flex items-center gap-3">
 <Calendar className="w-5 h-5 text-indigo-600"/>
 <span className="font-semibold text-slate-900">{monthData.month_label}</span>
 </div>
 <div className="flex items-center gap-4">
 {monthData.total_sales > 0 && (
 <div className="flex items-center gap-2">
 <DollarSign className="w-4 h-4 text-emerald-600"/>
 <span className="text-sm font-medium text-emerald-700">
 {t('salesDashboard.salesLabel')}: {formatCurrency(monthData.total_sales)}
 </span>
 </div>
 )}
 {monthData.total_rentals > 0 && (
 <div className="flex items-center gap-2">
 <Home className="w-4 h-4 text-blue-600"/>
 <span className="text-sm font-medium text-blue-700">
 {t('salesDashboard.rentLabel')}: {formatCurrency(monthData.total_rentals)}
 </span>
 </div>
 )}
 {expandedMonths[monthData.month_key] ? (
 <ChevronUp className="w-5 h-5 text-slate-400"/>
 ) : (
 <ChevronDown className="w-5 h-5 text-slate-400"/>
 )}
 </div>
 </button>

 {/* Month Details */}
 {expandedMonths[monthData.month_key] && (
 <div className="p-4 space-y-4">
 {/* Sales Payments */}
 {monthData.sales && monthData.sales.length > 0 && (
 <div>
 <div className="flex items-center gap-2 mb-2">
 <Building2 className="w-4 h-4 text-emerald-600"/>
 <h4 className="font-medium text-emerald-700">{t('salesDashboard.salesPayments')}</h4>
 </div>
 <div className="space-y-2 pl-6">
 {monthData.sales.map((payment, idx) => (
 <div
 key={`${payment.reservation_id}-${idx}`}
 className="flex items-center justify-between p-3 bg-emerald-50/80 rounded-sm border border-emerald-100"
 >
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <p className="font-medium text-slate-900">{payment.buyer_name}</p>
 <Badge variant="outline"className="text-xs bg-white">
 {payment.unit_code}
 </Badge>
 </div>
 <p className="text-[11px] text-slate-500 mt-1">
 {t('salesDashboard.due')}: {formatDate(payment.due_date)} • {payment.payment_type}
 </p>
 </div>
 <div className="flex items-center gap-2">
 <Badge variant="outline"className={getStatusColor(payment.status)}>
 {payment.status}
 </Badge>
 <span className="font-semibold text-emerald-700">
 {formatCurrency(payment.amount)}
 </span>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Rental Payments */}
 {monthData.rentals && monthData.rentals.length > 0 && (
 <div>
 <div className="flex items-center gap-2 mb-2">
 <Home className="w-4 h-4 text-blue-600"/>
 <h4 className="font-medium text-blue-700">{t('salesDashboard.rentalPayments')}</h4>
 </div>
 <div className="space-y-2 pl-6">
 {monthData.rentals.map((payment, idx) => (
 <div
 key={`${payment.rental_id}-${idx}`}
 className="flex items-center justify-between p-3 bg-blue-50/80 rounded-sm border border-blue-100"
 >
 <div className="flex-1">
 <div className="flex items-center gap-2">
 <p className="font-medium text-slate-900">{payment.tenant_name}</p>
 <Badge variant="outline"className="text-xs bg-white">
 {payment.unit_code}
 </Badge>
 </div>
 <p className="text-[11px] text-slate-500 mt-1">
 {t('salesDashboard.due')}: {formatDate(payment.due_date)} • {t('rentals.monthlyRent')}
 </p>
 </div>
 <div className="flex items-center gap-2">
 <Badge variant="outline"className={getStatusColor(payment.status)}>
 {payment.status}
 </Badge>
 <span className="font-semibold text-blue-700">
 {formatCurrency(payment.amount)}
 </span>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Empty state for month */}
 {(!monthData.sales || monthData.sales.length === 0) && 
 (!monthData.rentals || monthData.rentals.length === 0) && (
 <p className="text-sm text-slate-500 text-center py-2">
 {t('salesDashboard.noPaymentsThisMonth')}
 </p>
 )}
 </div>
 )}
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center py-8 text-slate-500">
 <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300"/>
 <p>{t('salesDashboard.noUpcomingPayments')}</p>
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 </Layout>
 );
};

export default SalesDashboard;
